import assign from 'assign-deep';
import { filter, sortBy } from 'lodash-es';
import moment from 'moment';
import { surveyTypesAsNames } from 'unihow-common';
import {
  questionTypeValues,
  schemaPartItemTypeValues,
  surveyPartTypeValues,
  surveyTypeValues,
  variableTypeValues
} from './constants/types';
import { getTranslation, getTranslationWithLocale } from './i18n';
import {
  API_DATE_FORMAT,
  AVAILABLE_LANGUAGES,
  routes,
  STUDENT_PSYCHOLOGIST_LINK_TAG,
  STUDENT_PSYCHOLOGIST_REGEX,
  surveySchemaActionTypes,
  TITLE_PREFIX
} from './constants';
import envConfig from './constants/env';

const periodKeys = ['common.periods.spring', 'common.periods.fall'];

export const getQuestionsWithResponseOptions = (schemaPartItem) => {
  const { responseOptions } = schemaPartItem.questionGroup;
  return schemaPartItem.questionGroup.questions
    .map((q) => ({ ...q, responseOptions }));
};

export const getSurveySchemaPartQuestions = (surveySchemaPart) =>
  surveySchemaPart.surveySchemaPartItems.reduce((acc, item) => {
    if (item.type === schemaPartItemTypeValues.QUESTION) {
      return acc.concat(item.question);
    }
    if (item.type === schemaPartItemTypeValues.QUESTION_GROUP) {
      return acc.concat(getQuestionsWithResponseOptions(item));
    }
    return acc;
  }, []);

export const getSurveySchemaPartAmount = (survey) => {
  const { length } = survey.surveySchemaParts;
  if (survey.extraSurveyPart) {
    return length + 1;
  }
  return length;
};

export const getVariablesWithQuestionResponseOptions = (surveySchemaPart) => {
  const likertQuestionsWithOptions = getSurveySchemaPartQuestions(surveySchemaPart)
    .filter((q) => questionTypeValues.LIKERT === q.type);
  return surveySchemaPart.variables
    .map((variable) => ({
      ...variable,
      questions: variable.questions.map((q) =>
        likertQuestionsWithOptions.find((lq) => lq.code === q.code))
    }));
};

export const getSurveyQuestions = (survey) => {
  if (survey) {
    return survey.surveySchemaParts
      .reduce((acc, part) => acc.concat(getSurveySchemaPartQuestions(part)), []);
  }
  return [];
};

export const hasQuestionType = (surveySchemaParts, targetType) => {
  if (!Array.isArray(surveySchemaParts)) return false;

  return surveySchemaParts.some((part) => {
    const items = part?.surveySchemaPartItems ?? [];
    return items.some((item) => {
      switch (item?.type) {
        case schemaPartItemTypeValues.QUESTION:
          return item?.question?.type === targetType;

        case schemaPartItemTypeValues.QUESTION_GROUP: {
          const questions = item?.questionGroup?.questions ?? [];
          return questions.some((q) => q?.type === targetType);
        }

        default:
          return false;
      }
    });
  });
};

export const getExtraSurveyQuestions = (survey) => (
  survey && survey.extraSurveyPart ? getSurveySchemaPartQuestions(survey.extraSurveyPart) : []);

export const getCommonName = ({ commonName, firstName, lastName }) => {
  if (commonName) {
    return commonName;
  }
  if (firstName && lastName) {
    return `${firstName} ${lastName}`;
  }
  return firstName || lastName || '';
};

export const isNumericQuestion = (question) => [
  questionTypeValues.DROPDOWN,
  questionTypeValues.RADIO_BUTTON_GROUP,
  questionTypeValues.CHECKBOX_GROUP,
  questionTypeValues.LIKERT
].includes(question.type);

export const assignArrayItemInObject = (array, index, changed) => {
  const newValue = assign({}, array[index], changed);
  return Object.assign([], array, { [index]: newValue });
};

// Replaces each :param in a route template with its URL-encoded value, e.g.
// ('/surveys/:surveyId', { surveyId: 1 }) -> '/surveys/1'. Only plain :name params are supported
// (no optional, splat or regex params), which is all the templates in `routes` use.
export const getCompiledPath = (template, parameters = {}) =>
  template.replace(/:(\w+)/g, (_, name) => {
    const value = parameters[name];
    if (!['string', 'number', 'boolean'].includes(typeof value) || value === '') {
      throw new TypeError(`Expected "${name}" to be a non-empty string, number or boolean`);
    }
    return encodeURIComponent(value);
  });

export const toLocaleNumber = (number, locale = 'fi') =>
  (number === null ? null : number.toLocaleString(locale, { maximumSignificantDigits: 2 }));

export const getLocalizedText = (text, currentLanguage, defaultText) => {
  if (text) {
    return getTranslation(text, currentLanguage) || defaultText;
  }

  return defaultText;
};

export const sortVariableStatisticsByType = (variableStatistics) =>
  [...sortBy(filter(variableStatistics, ['type', variableTypeValues.RELATIVE]), 'order'),
    ...sortBy(filter(variableStatistics, ['type', variableTypeValues.ABSOLUTE]), 'order')];

export const getSuffixedRoute = (from, to, suffix) => {
  const hasSuffix = from.endsWith(suffix);
  return `${to}${hasSuffix ? suffix : ''}`;
};

export const makeFormCacheKey = () => `form${new Date().getTime()}`;

// encodeURIComponent leaves !'()* unescaped; encode them too (RFC 3986)
const strictEncode = (value) => encodeURIComponent(value)
  .replace(/[!'()*]/g, (char) => `%${char.charCodeAt(0).toString(16).toUpperCase()}`);

const encodeURLParam = (key, value) =>
  (value === null ? strictEncode(key) : `${strictEncode(key)}=${strictEncode(value)}`);

// Same output as query-string@5 stringify({ encode: true }): sorted keys, undefined values
// skipped, null as a bare key, arrays as repeated keys, empty pairs dropped
export const stringifyURLParams = (params = {}) => Object.keys(params)
  .sort()
  .filter((key) => params[key] !== undefined)
  .map((key) => (Array.isArray(params[key])
    ? params[key]
      .filter((value) => value !== undefined)
      .map((value) => encodeURLParam(key, value))
      .join('&')
    : encodeURLParam(key, params[key])))
  .filter((pair) => pair.length > 0)
  .join('&');

export const validateApiDateStr = (date) =>
  date && moment(date, [API_DATE_FORMAT], true).isValid();

export const isSurveySchemaPartType = (type) =>
  type === surveyPartTypeValues.SURVEY_SCHEMA_PART;

export const getSurveyPartType = (path) => (
  path.includes(routes.surveySchemas.surveyParts.index)
    ? surveyPartTypeValues.SURVEY_SCHEMA_PART
    : surveyPartTypeValues.EXTRA_SURVEY_PART
);

export const arrayOverwritingMergeCustomizer = (objValue, srcValue) => {
  if (Array.isArray(objValue)) {
    return srcValue;
  }
  return undefined;
};

const getLocalizedPeriodNames = (year) =>
  periodKeys.map((period) =>
    AVAILABLE_LANGUAGES.reduce((acc, lang) =>
      Object.assign(acc, { [lang]: `${getTranslationWithLocale(period, lang)} ${year}` }), {}));

export const getTitlePeriods = () => {
  const now = new Date();
  const year = now.getFullYear();

  const currentYearPeriods = getLocalizedPeriodNames(year);
  const nextYearPeriods = getLocalizedPeriodNames(year + 1);

  // Spring period is between 1.1. and 31.7. Fall period is between 1.8. and 31.12.
  // Month is 0 indexed so august is 7th month
  // If fall period, don't return spring period for current year.
  return now.getMonth() < 7
    ? [...currentYearPeriods, ...nextYearPeriods]
    : [currentYearPeriods[1], ...nextYearPeriods];
};

export const getRouteSuffix = (route, depth = 1) => {
  const parts = route.split('/');
  const startIndex = Math.max(0, parts.length - depth);
  const suffix = parts.slice(startIndex).join('/');
  return `/${suffix}`;
};

export const parseTitle = (title) => {
  const lang = 'fi';
  const periods = periodKeys.map((periodKey) => getTranslationWithLocale(periodKey, lang)).join('|');
  const pattern = new RegExp(`((${periods}) \\d{4})( (.+))?`);
  const [, titlePeriodName, , , titleExtraPart = ''] = title?.fi?.match(pattern) || [];
  return {
    titlePeriodName,
    titleExtraPart
  };
};

export const requestPending = (state) =>
  Boolean(Object.entries(state).find(([key, val]) =>
    (val && (key === 'contentPending' || (typeof val === 'object' && requestPending(val))))));

export const setDocumentTitle = (title) => {
  if (title === TITLE_PREFIX) {
    document.title = TITLE_PREFIX;
  } else {
    document.title = `${title} - ${TITLE_PREFIX}`;
  }
};

export const getSubRouteForSurveyType = (surveyType) => {
  const routeMapping = {
    [surveyTypeValues.DEGREE_PROGRAM_SURVEY]: routes.surveys.hulSurveys,
    [surveyTypeValues.COURSE_SURVEY]: routes.surveys.husSurveys,
    [surveyTypeValues.HUP]: routes.surveys.hupSurveys,
    [surveyTypeValues.HUT]: routes.surveys.hutSurveys
  };
  return routeMapping[surveyType] || null;
};

export const getActionType = (isCreateNewCopy, isCreateNewVersion) => {
  let actionType;

  if (isCreateNewCopy) {
    actionType = surveySchemaActionTypes.COPY;
  } else if (isCreateNewVersion) {
    actionType = surveySchemaActionTypes.VERSION;
  } else {
    actionType = surveySchemaActionTypes.EDIT;
  }

  return actionType;
};

export const getSubRouteForActionType = (actionType) => {
  const routeMapping = {
    [surveySchemaActionTypes.COPY]: routes.surveySchemas.schemas.schema.copy,
    [surveySchemaActionTypes.VERSION]: routes.surveySchemas.schemas.schema.newVersion,
    [surveySchemaActionTypes.EDIT]: routes.surveySchemas.schemas.schema.edit
  };
  return routeMapping[actionType] || null;
};

/**
 * Sets a tabIndex for the element with the provided ID and focuses the same element
 * @param  {String} elementId     id for the element to be searched for.
 * @param  {Number} setTabIndex   set a tabIndex for the element, defaults to -1 to prevent keyboard navigation to it
 */
export const autoFocusElementById = (elementId, setTabIndex = -1) => {
  const element = document.getElementById(elementId);
  if (element) {
    element.tabIndex = setTabIndex;
    element.focus();
  } else if (!element && process.env.NODE_ENV === 'development') {
    // Logging this as an error as this function is used for a lot of accessibility stuff
    // eslint-disable-next-line no-console
    console.error(`autoFocusElementById could not find an element with the id: ${elementId}`);
  }
};

const formatImprovementSuggestions = (translatedSuggestions, translatedLink) => {
  /* Translated suggestions is one long string with html tags and we want to ensure that link formatting is in place when using environment variable links.
  Users are able to edit and remove link formatting. */
  const forcedLinkFormatting = `<a href=${translatedLink}>${translatedLink}</a>`;

  if (!translatedSuggestions) return null;

  const hasLinkFormatting = translatedSuggestions.includes(STUDENT_PSYCHOLOGIST_LINK_TAG);
  const link = hasLinkFormatting ? translatedLink : forcedLinkFormatting;

  // Replace student psychologist variable with the actual link from env config.
  return translatedSuggestions.replace(
    STUDENT_PSYCHOLOGIST_REGEX,
    link
  );
};

export const replaceStudentPsychologistLink = (improvementSuggestions, lang) => {
  const translatedStudentPsychologistLink = getTranslation(envConfig.STUDENT_PSYCHOLOGIST_LINK, lang);
  const translatedImprovementSuggestions = getTranslation(improvementSuggestions, lang);

  return formatImprovementSuggestions(translatedImprovementSuggestions, translatedStudentPsychologistLink);
};

export const navigateTo = (navigate, template, survey) => {
  navigate(getCompiledPath(template, { surveyId: survey }));
};

export const isUserLoggedIn = (user) => user?.name?.length;

export const triggerFileDownload = (blob, filename) => {
  const downloadUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = downloadUrl;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();

  // Clean up
  link.remove();
  window.URL.revokeObjectURL(downloadUrl);
};

export const isSurveyForDegreeProgram = (surveyType) => [
  surveyTypeValues.DEGREE_PROGRAM_SURVEY,
  surveyTypeValues.HUP
].includes(surveyType);

export const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

export const getLineCount = (str) => {
  if (str.length === 0) return 0;
  const lines = str.split(/\r\n|\r|\n/); // split on CRLF, CR or LF
  return lines.length;
};

export const objHasKey = (obj, k) => obj != null && Object.prototype.hasOwnProperty.call(obj, k);

export const formatRoleLabel = (role, t) => {
  const { roleName, roleKind, surveyType } = role;

  if (!surveyType) {
    return t(`generalRoles.${roleName}`);
  }
  return `${surveyTypesAsNames[surveyType]} - ${t(`roleKinds.${roleKind}`)}`;
};
